import React from "react";
import { cn, Text } from "@fmc-digital/ui";
import { StepperBackIcon } from "./icons/StepperBackIcon";
import { StepTickIcon } from "./icons/StepTickIcon";
import { EmailIcon } from "./icons/EmailIcon";
import { PersonalInfoIcon } from "./icons/PersonalInfoIcon";
import { MobileIcon } from "./icons/MobileIcon";
import { IconButton } from "@mui/material";
import { useIsMinWidth768 } from "@profile/hooks/useIsMinWidth768";
import {
  REGISTER_STEPPER_CIRCLE_SIZE,
  REGISTER_STEPPER_LABEL_LINE_HEIGHT,
  REGISTER_STEPPER_LABEL_OFFSET,
  registerStepperBackButtonStyles,
  registerStepperCircleStyles,
  registerStepperCircleStateStyles,
  registerStepperConnectorStyles,
  registerStepperGlyphStyles,
  registerStepperLabelStyles,
} from "./register-flow.styles";

interface Step {
  name: string;
  title: string;
  icon: React.ReactNode;
}

interface StepperProcessProps {
  currentStep: number;
  onBack?: () => void;
  steps?: Step[];
}

const glyphClassName = registerStepperGlyphStyles();

const defaultSteps: Step[] = [
  { name: "email", title: "Email", icon: <EmailIcon className={glyphClassName} /> },
  { name: "personalInfo", title: "Personal Info", icon: <PersonalInfoIcon className={glyphClassName} /> },
  { name: "phone", title: "Phone", icon: <MobileIcon className={glyphClassName} /> },
];

function applyGlyphClass(icon: React.ReactNode): React.ReactNode {
  if (!React.isValidElement(icon)) return icon;
  return React.cloneElement(
    icon as React.ReactElement<{ className?: string }>,
    { className: cn(glyphClassName, (icon as React.ReactElement<{ className?: string }>).props.className) },
  );
}

function getCircleState(isCompleted: boolean, isCurrent: boolean): "completed" | "current" | "inactive" {
  if (isCompleted) return "completed";
  if (isCurrent) return "current";
  return "inactive";
}

export function StepperProcess({ currentStep, onBack, steps = defaultSteps }: Readonly<StepperProcessProps>) {
  const isMdUp = useIsMinWidth768();
  const isMobileActiveLabelOnly = !isMdUp;
  const stepIconSize = isMdUp ? REGISTER_STEPPER_CIRCLE_SIZE.desktop : REGISTER_STEPPER_CIRCLE_SIZE.mobile;
  const stepGap = isMdUp ? 61 : 40;
  const stepperWidth = stepIconSize * steps.length + stepGap * (steps.length - 1);
  const labelLineHeight = isMdUp
    ? REGISTER_STEPPER_LABEL_LINE_HEIGHT.desktop
    : REGISTER_STEPPER_LABEL_LINE_HEIGHT.mobile;
  const stepperContentHeight =
    stepIconSize + REGISTER_STEPPER_LABEL_OFFSET + labelLineHeight;

  return (
    <div
      className="relative flex flex-col items-center w-full pt-6 pb-0"
      data-testid="stepper-process-root"
    >
      <div
        className="relative flex w-full max-w-full items-start justify-center"
        style={{
          height: stepperContentHeight,
          minHeight: stepperContentHeight,
        }}
        data-testid="stepper-content"
        data-align="start"
      >
        {onBack && (
          <IconButton
            onClick={onBack}
            className={cn(registerStepperBackButtonStyles(), "mr-2")}
            style={{ position: "absolute", left: 0, top: stepIconSize / 2, transform: "translateY(-50%)" }}
            aria-label="Back"
            data-testid="stepper-back-button"
          >
            <StepperBackIcon className={glyphClassName} />
          </IconButton>
        )}
        <div
          className="relative flex items-center justify-center max-w-full mx-auto"
          style={{ width: stepperWidth, gap: stepGap, height: stepIconSize }}
          data-testid="stepper-row"
          data-step-gap={stepGap}
          data-step-icon-size={stepIconSize}
          data-mobile-active-label-only={isMobileActiveLabelOnly}
        >
          {steps.map((step, idx) => {
            if (idx === steps.length - 1) return null;
            const nextStep = steps[idx + 1];
            return (
              <div
                key={`line-${step.name}-to-${nextStep.name}`}
                className={cn(registerStepperConnectorStyles())}
                style={{
                  position: "absolute",
                  left: stepIconSize / 2 + idx * (stepIconSize + stepGap),
                  top: stepIconSize / 2 - 2,
                  width: stepGap + stepIconSize,
                  zIndex: 0,
                }}
              />
            );
          })}
          {steps.map((step, idx) => {
            const isCompleted = idx < currentStep;
            const isCurrent = idx === currentStep;
            const circleState = getCircleState(isCompleted, isCurrent);
            return (
              <div
                key={step.name}
                className="flex items-center justify-center"
                style={{
                  minWidth: stepIconSize,
                  height: stepIconSize,
                  position: "relative",
                  zIndex: 1,
                  overflow: "visible",
                }}
              >
                <div
                  className={cn(
                    registerStepperCircleStyles(),
                    registerStepperCircleStateStyles({ state: circleState }),
                  )}
                >
                  {isCompleted ? (
                    <StepTickIcon className={glyphClassName} />
                  ) : (
                    applyGlyphClass(step.icon)
                  )}
                </div>
                {(isMdUp || isCurrent) && (
                  <Text
                    as="span"
                    className={cn(registerStepperLabelStyles())}
                    style={{
                      position: "absolute",
                      top: stepIconSize + REGISTER_STEPPER_LABEL_OFFSET,
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "max-content",
                    }}
                  >
                    {step.title}
                  </Text>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
